import React, { useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import moment from 'moment';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import { AppointmentSummaryResponse } from '../../../../../shared/api/controllers/medicalIntegrationConfiguration';
import { calculateOutboundAppointmentSuccess, getOutboundAppointmentSummaryDisplay } from '../../../../../shared/utils/appointmentSummary';
import {
    color_defaults_white,
    color_shades_darkest,
    color_shades_light,
    color_shades_lighter,
    voicify_spark_dark,
    voicify_spark_darker,
    voicify_spark_light,
    voicify_spark_lightest
} from '../../../../../shared/constants/colors';
import { breakpoint_large, breakpoint_small } from '../../../../../shared/constants/breakpoints';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';

interface AppointmentSummaryModalProps {
    isVisible: boolean;
    onClose: () => void;
    summary: AppointmentSummaryResponse | null;
    startDate: string;
    endDate: string;
    isLoading: boolean;
    hasFailedBatches: boolean;
    outboundCallCount?: number;
}

type MetricTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface SummaryMetricProps {
    label: string;
    value: number;
    description: string;
    tone?: MetricTone;
    meta?: string;
    metaTooltip?: string;
}

interface BreakdownItem {
    label: string;
    value: number;
    tone: MetricTone;
}

const modalLayoutClass = css`
    width: min(1040px, calc(100vw - 40px)) !important;
    height: auto !important;
    max-height: calc(100vh - 40px) !important;
    border: 1px solid rgba(50, 70, 97, 0.12) !important;
    border-radius: 24px !important;
    box-shadow: 0 28px 80px rgba(23, 42, 68, 0.28) !important;

    @media (max-width: 767px) {
        width: calc(100vw - 20px) !important;
        max-height: calc(100vh - 20px) !important;
        border-radius: 20px !important;
    }
`;

const formatPercent = (numerator: number, denominator: number): string => {
    if (denominator <= 0) return '0%';
    return `${Math.round((numerator / denominator) * 100)}%`;
};

const SummaryMetric: React.FC<SummaryMetricProps> = ({
    label,
    value,
    description,
    tone = 'neutral',
    meta,
    metaTooltip
}) => (
    <MetricCard tone={tone}>
        <MetricValue>{value.toLocaleString()}</MetricValue>
        <MetricLabel>{label}</MetricLabel>
        {meta && (
            <MetricMeta tone={tone}>
                <span>{meta}</span>
                {metaTooltip && (
                    <TooltipWrapper text={metaTooltip} place="top" maxWidth={300}>
                        <MetricMetaInfo aria-label={metaTooltip}>i</MetricMetaInfo>
                    </TooltipWrapper>
                )}
            </MetricMeta>
        )}
        <MetricDescription>{description}</MetricDescription>
    </MetricCard>
);

const AppointmentSummaryModal: React.FC<AppointmentSummaryModalProps> = ({
    isVisible,
    onClose,
    summary,
    startDate,
    endDate,
    isLoading,
    hasFailedBatches,
    outboundCallCount
}) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const successMetrics = useMemo(
        () => calculateOutboundAppointmentSuccess(summary),
        [summary]
    );

    useEffect(() => {
        if (isVisible) {
            closeButtonRef.current?.focus();
        }
    }, [isVisible]);

    if (!summary) return null;

    const totalAppointments = summary.totalAppointments ?? 0;
    const messagesLeft = summary.leftMessageByVoicify ?? 0;
    const confirmedByVoicifyAndCheckedOut = summary.confirmedByVoicifyAndCheckedOut ?? 0;
    const confirmedByVoicifyAfterMessage = summary.confirmedByVoicifyAfterLeftMessage ?? 0;
    const confirmedAfterMessage = summary.confirmedAfterVoicifyLeftMessage ?? 0;
    const missedAfterMessage = summary.missedAfterVoicifyLeftMessage ?? 0;
    const missedWithBooking = summary.missedWithSubsequentBooking ?? 0;
    const missedBookedAfterVoicify = summary.missedWithBookingAfterVoicifyCall ?? 0;
    const missedPatientsCalled = summary.missedPatientsCalled ?? summary.totalMissed ?? 0;
    const missedPatientsWithBooking = summary.missedPatientsWithBooking ?? missedWithBooking;
    const missedPatientsWithVoicifyBooking = summary.missedPatientsWithVoicifyBooking ?? missedBookedAfterVoicify;
    const missedPatientsBookedAnotherWay = Math.max(
        missedPatientsWithBooking - missedPatientsWithVoicifyBooking,
        0
    );
    const totalReactivationPatients = summary.totalReactivationPatients ?? 0;
    const reactivationPatientsWithBooking = summary.reactivationPatientsWithBooking ?? 0;
    const reactivationPatientsWithVoicifyBooking = summary.reactivationPatientsWithVoicifyBooking ?? 0;
    const reactivationPatientsBookedAnotherWay = Math.max(
        reactivationPatientsWithBooking - reactivationPatientsWithVoicifyBooking,
        0
    );
    const hasReactivationData =
        totalReactivationPatients > 0 ||
        reactivationPatientsWithBooking > 0 ||
        reactivationPatientsWithVoicifyBooking > 0;
    const rate = successMetrics.rate;
    const emptySummaryDescription = getOutboundAppointmentSummaryDisplay(summary, outboundCallCount).description;
    const dateRange = `${moment(startDate).format('MMM D, YYYY')} – ${moment(endDate).format('MMM D, YYYY')}`;

    const hasExactFinalOutcomes = summary.totalOther !== undefined;
    const knownFinalOutcomesWithoutMissed =
        (summary.totalConfirmed ?? 0) +
        (summary.totalCheckedOut ?? 0) +
        (summary.totalReschedules ?? 0) +
        (summary.totalCancelled ?? 0) +
        (summary.totalLeftMessage ?? 0);
    const availableFinalOutcomeSlots = Math.max(totalAppointments - knownFinalOutcomesWithoutMissed, 0);
    const finalMissed = hasExactFinalOutcomes
        ? summary.totalMissed ?? 0
        : Math.min(summary.totalMissed ?? 0, availableFinalOutcomeSlots);
    const finalOther = hasExactFinalOutcomes
        ? summary.totalOther ?? 0
        : Math.max(availableFinalOutcomeSlots - finalMissed, 0);

    const finalOutcomes: BreakdownItem[] = [
        { label: 'Confirmed', value: summary.totalConfirmed ?? 0, tone: 'success' },
        { label: 'Checked out', value: summary.totalCheckedOut ?? 0, tone: 'success' },
        { label: 'Rescheduled', value: summary.totalReschedules ?? 0, tone: 'warning' },
        { label: 'Cancelled', value: summary.totalCancelled ?? 0, tone: 'danger' },
        { label: 'Left message', value: summary.totalLeftMessage ?? 0, tone: 'warning' },
        { label: 'Missed', value: finalMissed, tone: 'danger' },
        { label: 'Other / pending', value: finalOther, tone: 'neutral' }
    ];
    const finalOutcomeTotal = finalOutcomes.reduce((total, outcome) => total + outcome.value, 0);

    const hasExactMessageOutcomes = summary.messageOutcomeConfirmed !== undefined;
    const fallbackMessageOther = Math.max(messagesLeft - confirmedAfterMessage - missedAfterMessage, 0);
    const messageOutcomes: BreakdownItem[] = hasExactMessageOutcomes
        ? [
            { label: 'Confirmed', value: summary.messageOutcomeConfirmed ?? 0, tone: 'success' },
            { label: 'Checked out', value: summary.messageOutcomeCheckedOut ?? 0, tone: 'success' },
            { label: 'Rescheduled', value: summary.messageOutcomeRescheduled ?? 0, tone: 'warning' },
            { label: 'Cancelled', value: summary.messageOutcomeCancelled ?? 0, tone: 'danger' },
            { label: 'Still left message', value: summary.messageOutcomeLeftMessage ?? 0, tone: 'warning' },
            { label: 'Missed', value: summary.messageOutcomeMissed ?? 0, tone: 'danger' },
            { label: 'Other / pending', value: summary.messageOutcomeOther ?? 0, tone: 'neutral' }
        ]
        : [
            { label: 'Confirmed later', value: confirmedAfterMessage, tone: 'success' },
            { label: 'Missed later', value: missedAfterMessage, tone: 'danger' },
            { label: 'Other / pending', value: fallbackMessageOther, tone: 'neutral' }
        ];
    const messageOutcomeTotal = messageOutcomes.reduce((total, outcome) => total + outcome.value, 0);

    return (
        <ModalLayout
            isVisible={isVisible}
            onClose={onClose}
            className={modalLayoutClass}
            zIndex={1200}
        >
            <ModalContent role="dialog" aria-labelledby="appointment-summary-modal-title">
                <ModalHeader>
                    <HeaderCopy>
                        <Eyebrow>Outbound appointment performance</Eyebrow>
                        <ModalTitle id="appointment-summary-modal-title">Appointment outcomes</ModalTitle>
                        <DateRange>{dateRange}</DateRange>
                    </HeaderCopy>
                    <CloseButton
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        aria-label="Close appointment outcomes"
                    >
                        ×
                    </CloseButton>
                </ModalHeader>

                {(isLoading || hasFailedBatches) && (
                    <LoadingNotice role="status" warning={!isLoading && hasFailedBatches}>
                        <LoadingNoticeDot active={isLoading} warning={!isLoading && hasFailedBatches} aria-hidden="true" />
                        {isLoading
                            ? 'Results are still loading. Totals shown are not complete yet.'
                            : 'Partial results: All totals and percentages below reflect only the appointment data received and may be incomplete.'}
                    </LoadingNotice>
                )}

                <ModalBody>
                    <SuccessPanel>
                        <SuccessRateColumn>
                            <SuccessRate>{rate === null ? '—' : `${rate}%`}</SuccessRate>
                            <SuccessRateLabel>Outbound success rate</SuccessRateLabel>
                        </SuccessRateColumn>
                        <SuccessExplanation>
                            <SuccessSentence>
                                {rate === null ? emptySummaryDescription : <>
                                    <strong>{successMetrics.successfulOutcomes.toLocaleString()}</strong> successful outcomes
                                    from <strong>{successMetrics.denominator.toLocaleString()}</strong> appointments included in the rate.
                                </>}
                            </SuccessSentence>
                            <ProgressTrack aria-hidden="true">
                                <ProgressFill width={Math.min(Math.max(rate ?? 0, 0), 100)} />
                            </ProgressTrack>
                            <SuccessNote>
                                The rate excludes appointments whose only final result was a left message. Successful outcomes
                                include confirmed, checked-out, rescheduled, cancelled, and recovered missed appointments.
                            </SuccessNote>
                        </SuccessExplanation>
                    </SuccessPanel>

                    <ScopeFlow aria-label="How the outbound success rate is calculated">
                        <FlowStep tone="info">
                            <FlowValue>{totalAppointments.toLocaleString()}</FlowValue>
                            <FlowLabel>Total appointments</FlowLabel>
                            <FlowDescription>
                                PMS appointments matched to phone numbers in Voicify's outbound call data.
                            </FlowDescription>
                        </FlowStep>
                        <FlowStep tone="warning">
                            <FlowValue>{successMetrics.denominator.toLocaleString()}</FlowValue>
                            <FlowLabel>Appointments counted in the rate</FlowLabel>
                            <FlowDescription>
                                Total appointments excluding those whose only final result was a left message.
                            </FlowDescription>
                        </FlowStep>
                        <FlowStep tone="success">
                            <FlowValue>{successMetrics.successfulOutcomes.toLocaleString()}</FlowValue>
                            <FlowLabel>Successful outcomes</FlowLabel>
                            <FlowDescription>
                                Confirmed, checked out, rescheduled, cancelled, or recovered after a missed visit.
                            </FlowDescription>
                        </FlowStep>
                    </ScopeFlow>

                    <Section>
                        <SectionHeading>
                            <div>
                                <SectionTitle>How Voicify influenced appointments</SectionTitle>
                            </div>
                            <SectionSubtitle>Lifecycle activity, not additional final appointments</SectionSubtitle>
                        </SectionHeading>
                        <RelationshipNote>
                            These are actions Voicify handled or influenced. They are subsets of the matched appointments
                            and should not be added to the final-status total below.
                        </RelationshipNote>
                        <InfluenceGrid>
                            <SummaryMetric
                                label="Confirmed during the call"
                                value={summary.confirmedByVoicify ?? 0}
                                description="The patient confirmed while speaking with Voicify."
                                tone="success"
                                meta={`${confirmedByVoicifyAndCheckedOut.toLocaleString()} ultimately checked out`}
                                metaTooltip="This count may be lower when confirmed appointments are still upcoming. An appointment is counted here only after the visit occurs."
                            />
                            <SummaryMetric
                                label="Messages left"
                                value={messagesLeft}
                                description={`${confirmedByVoicifyAfterMessage.toLocaleString()} ${confirmedByVoicifyAfterMessage === 1 ? 'appointment was' : 'appointments were'} confirmed by Voicify during a later call after a message was left. Final PMS results are partitioned below.`}
                                tone="warning"
                                meta={`${confirmedAfterMessage.toLocaleString()} later confirmed · ${missedAfterMessage.toLocaleString()} later missed`}
                            />
                        </InfluenceGrid>

                        <JourneyPanel>
                            <JourneyHeader>
                                <div>
                                    <JourneyTitle>What happened after a message?</JourneyTitle>
                                    <JourneyDescription>
                                        Each message is placed in one final-status bucket so the breakdown balances.
                                    </JourneyDescription>
                                </div>
                                <JourneyTotal>
                                    <strong>{messageOutcomeTotal.toLocaleString()}</strong>
                                    <span>of {messagesLeft.toLocaleString()} classified</span>
                                </JourneyTotal>
                            </JourneyHeader>
                            <SegmentedBar aria-hidden="true">
                                {messageOutcomes.filter(outcome => outcome.value > 0).map(outcome => (
                                    <Segment
                                        key={outcome.label}
                                        tone={outcome.tone}
                                        width={messagesLeft > 0 ? (outcome.value / messagesLeft) * 100 : 0}
                                    />
                                ))}
                            </SegmentedBar>
                            <BreakdownGrid>
                                {messageOutcomes.map(outcome => (
                                    <BreakdownCard key={outcome.label} tone={outcome.tone}>
                                        <BreakdownValue>{outcome.value.toLocaleString()}</BreakdownValue>
                                        <BreakdownLabel>{outcome.label}</BreakdownLabel>
                                        <BreakdownPercent>{formatPercent(outcome.value, messagesLeft)}</BreakdownPercent>
                                    </BreakdownCard>
                                ))}
                            </BreakdownGrid>
                        </JourneyPanel>
                    </Section>

                    <Section>
                        <SectionHeading>
                            <div>
                                <SectionTitle>Final appointment status</SectionTitle>
                            </div>
                            <ReconciledBadge reconciled={finalOutcomeTotal === totalAppointments}>
                                {finalOutcomeTotal.toLocaleString()} of {totalAppointments.toLocaleString()} categorized
                            </ReconciledBadge>
                        </SectionHeading>
                        <RelationshipNote>
                            This is a mutually exclusive snapshot: every matched appointment appears exactly once.
                            It includes outcomes completed by staff, patients, and Voicify.
                        </RelationshipNote>
                        <MetricsGrid>
                            <SummaryMetric
                                label="Confirmed"
                                value={summary.totalConfirmed ?? 0}
                                description="Final PMS status is confirmed."
                                tone="success"
                            />
                            <SummaryMetric
                                label="Checked out"
                                value={summary.totalCheckedOut ?? 0}
                                description="The appointment was completed."
                                tone="success"
                            />
                            <SummaryMetric
                                label="Rescheduled"
                                value={summary.totalReschedules ?? 0}
                                description="Moved to a new date or time."
                                tone="warning"
                            />
                            <SummaryMetric
                                label="Cancelled"
                                value={summary.totalCancelled ?? 0}
                                description="Final PMS status is cancelled."
                                tone="danger"
                            />
                            <SummaryMetric
                                label="Left message"
                                value={summary.totalLeftMessage ?? 0}
                                description="No later final response was recorded."
                                tone="warning"
                            />
                            <SummaryMetric
                                label="Missed"
                                value={finalMissed}
                                description="Final PMS status is missed."
                                tone="danger"
                            />
                            <SummaryMetric
                                label="Other / pending"
                                value={finalOther}
                                description="Scheduled, pending, or an unmapped PMS status."
                                tone="neutral"
                            />
                        </MetricsGrid>
                        <FormulaBar>
                            <FormulaLabel>Reconciliation</FormulaLabel>
                            <FormulaValues>
                                {finalOutcomes.map(outcome => outcome.value.toLocaleString()).join(' + ')}
                                <strong>= {finalOutcomeTotal.toLocaleString()} total appointments</strong>
                            </FormulaValues>
                        </FormulaBar>
                    </Section>

                    <Section>
                        <SectionHeading>
                            <div>
                                <SectionTitle>Missed appointment follow-up</SectionTitle>
                            </div>
                            <SectionSubtitle>A lifecycle subset of appointments marked missed</SectionSubtitle>
                        </SectionHeading>
                        <RelationshipNote>
                            Follow-up results describe what happened after a missed visit. They are not additional final statuses.
                        </RelationshipNote>
                        <MetricsGrid>
                            <SummaryMetric
                                label="Patients called"
                                value={missedPatientsCalled}
                                description="Patients Voicify called about a missed appointment."
                                tone="info"
                            />
                            <SummaryMetric
                                label="Booked by Voicify"
                                value={missedPatientsWithVoicifyBooking}
                                description="Patients whose new appointment was created by Voicify during follow-up."
                                tone="success"
                                meta={`${formatPercent(missedPatientsWithVoicifyBooking, missedPatientsCalled)} of called patients`}
                            />
                            <SummaryMetric
                                label="Booked another way"
                                value={missedPatientsBookedAnotherWay}
                                description="Patients who booked after outreach, but the booking was not created by Voicify."
                                tone="info"
                                meta={`${formatPercent(missedPatientsBookedAnotherWay, missedPatientsCalled)} of called patients`}
                            />
                        </MetricsGrid>
                    </Section>

                    {hasReactivationData && (
                        <Section>
                            <SectionHeading>
                                <div>
                                    <SectionTitle>Reactivation follow-up</SectionTitle>
                                </div>
                            </SectionHeading>
                            <RelationshipNote>
                                Reactivation results show how patients booked after Voicify outreach and whether Voicify created the booking.
                            </RelationshipNote>
                            <MetricsGrid>
                                <SummaryMetric
                                    label="Patients called"
                                    value={totalReactivationPatients}
                                    description="Patients Voicify called for reactivation outreach."
                                    tone="info"
                                />
                                <SummaryMetric
                                    label="Booked by Voicify"
                                    value={reactivationPatientsWithVoicifyBooking}
                                    description="Patients whose new appointment was created by Voicify during reactivation outreach."
                                    tone="success"
                                    meta={`${formatPercent(reactivationPatientsWithVoicifyBooking, totalReactivationPatients)} of called patients`}
                                />
                                <SummaryMetric
                                    label="Booked another way"
                                    value={reactivationPatientsBookedAnotherWay}
                                    description="Patients who booked after outreach, but the booking was not created by Voicify."
                                    tone="info"
                                    meta={`${formatPercent(reactivationPatientsBookedAnotherWay, totalReactivationPatients)} of called patients`}
                                />
                            </MetricsGrid>
                        </Section>
                    )}
                </ModalBody>
            </ModalContent>
        </ModalLayout>
    );
};

const toneColor = (tone: MetricTone): string => {
    switch (tone) {
        case 'success': return '#0E85BB';
        case 'info': return '#1BA5DC';
        case 'warning': return '#718096';
        case 'danger': return '#475569';
        default: return '#8F97A1';
    }
};

const toneBackground = (tone: MetricTone): string => {
    switch (tone) {
        case 'success': return '#F1F9FE';
        case 'info': return '#F5FAFD';
        case 'warning': return '#F7F8FA';
        case 'danger': return '#F4F6F8';
        default: return '#FCFDFE';
    }
};

const toneBorder = (tone: MetricTone): string => {
    switch (tone) {
        case 'success': return 'rgba(14, 133, 187, 0.18)';
        case 'info': return 'rgba(27, 165, 220, 0.16)';
        case 'warning': return 'rgba(71, 85, 105, 0.14)';
        case 'danger': return 'rgba(71, 85, 105, 0.2)';
        default: return 'rgba(50, 70, 97, 0.12)';
    }
};

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: calc(100vh - 42px);
    overflow: hidden;
    color: ${color_shades_darkest};
    background: ${color_defaults_white};
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 30px 20px;
    border-bottom: 1px solid rgba(50, 70, 97, 0.09);
    background: rgba(255, 255, 255, 0.96);
    flex-shrink: 0;

    ${breakpoint_small} {
        padding: 20px;
    }
`;

const LoadingNotice = styled.div<{ warning?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 30px;
    border-bottom: 1px solid ${props => props.warning ? 'rgba(244, 197, 66, 0.45)' : 'rgba(27, 165, 220, 0.12)'};
    color: ${props => props.warning ? '#765b16' : '#59677a'};
    background: ${props => props.warning ? '#fff8df' : voicify_spark_lightest};
    font-size: 12px;
    font-weight: 550;
    flex-shrink: 0;

    ${breakpoint_small} {
        padding: 9px 20px;
    }
`;

const LoadingNoticeDot = styled.span<{ active?: boolean; warning?: boolean }>`
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    border-radius: 50%;
    background: ${props => props.warning ? '#f4c542' : voicify_spark_dark};
    border: 1px solid ${props => props.warning ? '#a88418' : 'transparent'};
    animation: ${props => props.active ? 'appointmentSummaryModalPulse 1.4s ease-in-out infinite' : 'none'};

    @keyframes appointmentSummaryModalPulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.9; }
    }
`;

const HeaderCopy = styled.div`
    min-width: 0;
`;

const Eyebrow = styled.div`
    margin-bottom: 5px;
    color: ${voicify_spark_darker};
    font-size: 11px;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const ModalTitle = styled.h2`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 27px;
    font-weight: 720;
    letter-spacing: -0.6px;
    line-height: 1.15;
`;

const DateRange = styled.div`
    margin-top: 6px;
    color: #59677a;
    font-size: 12px;
    font-weight: 550;
`;

const CloseButton = styled.button`
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    margin-left: 20px;
    padding: 0 0 2px;
    border: 0;
    border-radius: 50%;
    background: ${color_shades_lighter};
    color: #59677a;
    cursor: pointer;
    font-size: 24px;
    line-height: 1;
    transition: background 160ms ease, color 160ms ease, transform 160ms ease;

    &:hover {
        background: ${color_shades_light};
        color: ${color_shades_darkest};
        transform: scale(1.04);
    }

    &:focus-visible {
        outline: 3px solid ${voicify_spark_light};
        outline-offset: 2px;
    }
`;

const ModalBody = styled.div`
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 26px 30px 56px;
    background: #fbfcfd;
    scroll-padding-bottom: 56px;

    ${breakpoint_small} {
        padding: 18px 16px 44px;
    }
`;

const SuccessPanel = styled.div`
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 30px;
    align-items: center;
    padding: 24px 26px;
    border: 1px solid rgba(27, 165, 220, 0.2);
    border-radius: 20px;
    background: linear-gradient(135deg, ${voicify_spark_lightest} 0%, ${color_defaults_white} 75%);
    box-shadow: 0 10px 32px rgba(44, 105, 137, 0.07);

    ${breakpoint_small} {
        grid-template-columns: 1fr;
        gap: 16px;
        padding: 20px;
    }
`;

const SuccessRateColumn = styled.div`
    padding-right: 26px;
    border-right: 1px solid rgba(27, 165, 220, 0.2);

    ${breakpoint_small} {
        padding: 0 0 16px;
        border-right: 0;
        border-bottom: 1px solid rgba(27, 165, 220, 0.2);
    }
`;

const SuccessRate = styled.div`
    color: ${color_shades_darkest};
    font-size: 52px;
    font-weight: 760;
    letter-spacing: -2px;
    line-height: 1;

    ${breakpoint_large} {
        font-size: 44px;
    }
`;

const SuccessRateLabel = styled.div`
    margin-top: 9px;
    color: #455468;
    font-size: 12px;
    font-weight: 700;
`;

const SuccessExplanation = styled.div`
    min-width: 0;
`;

const SuccessSentence = styled.div`
    color: #455468;
    font-size: 15px;
    line-height: 1.45;

    strong {
        color: ${color_shades_darkest};
        font-weight: 750;
    }
`;

const ProgressTrack = styled.div`
    height: 8px;
    margin: 14px 0 11px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(27, 165, 220, 0.14);
`;

const ProgressFill = styled.div<{ width: number }>`
    width: ${props => props.width}%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${voicify_spark_darker}, ${voicify_spark_dark});
`;

const SuccessNote = styled.div`
    color: #59677a;
    font-size: 11px;
    line-height: 1.55;
`;

const ScopeFlow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    align-items: stretch;
    margin-top: 14px;

    ${breakpoint_small} {
        grid-template-columns: 1fr;
    }
`;

const FlowStep = styled.div<{ tone: MetricTone }>`
    padding: 15px 17px;
    border: 1px solid ${props => toneBorder(props.tone)};
    border-radius: 14px;
    background: ${props => toneBackground(props.tone)};
`;

const FlowValue = styled.div`
    color: ${color_shades_darkest};
    font-size: 25px;
    font-weight: 750;
    line-height: 1;
`;

const FlowLabel = styled.div`
    margin-top: 5px;
    color: ${color_shades_darkest};
    font-size: 12px;
    font-weight: 720;
    line-height: 1.3;
`;

const FlowDescription = styled.div`
    margin-top: 5px;
    color: #59677a;
    font-size: 10px;
    line-height: 1.45;
`;

const Section = styled.section`
    margin-top: 34px;
`;

const SectionHeading = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 11px;

    ${breakpoint_small} {
        display: block;
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 17px;
    font-weight: 720;
    letter-spacing: -0.25px;
`;

const SectionSubtitle = styled.div`
    color: #59677a;
    font-size: 11px;
    text-align: right;

    ${breakpoint_small} {
        margin-top: 5px;
        text-align: left;
    }
`;

const RelationshipNote = styled.div`
    margin-bottom: 13px;
    padding: 10px 12px;
    border-left: 3px solid #9fbbca;
    border-radius: 0 8px 8px 0;
    background: #f3f7f9;
    color: #4a596c;
    font-size: 11px;
    line-height: 1.5;
`;

const InfluenceGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    ${breakpoint_small} {
        grid-template-columns: 1fr;
    }
`;

const JourneyPanel = styled.div`
    margin-top: 14px;
    padding: 17px;
    border: 1px solid #d8dee6;
    border-radius: 16px;
    background: ${color_defaults_white};
`;

const JourneyHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;

    ${breakpoint_small} {
        display: block;
    }
`;

const JourneyTitle = styled.div`
    color: ${color_shades_darkest};
    font-size: 13px;
    font-weight: 720;
`;

const JourneyDescription = styled.div`
    margin-top: 3px;
    color: #59677a;
    font-size: 11px;
    line-height: 1.45;
`;

const JourneyTotal = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
    color: #59677a;
    font-size: 10px;

    strong {
        color: ${color_shades_darkest};
        font-size: 16px;
    }

    ${breakpoint_small} {
        margin-top: 8px;
    }
`;

const SegmentedBar = styled.div`
    display: flex;
    height: 9px;
    gap: 2px;
    margin: 14px 0;
    overflow: hidden;
    border-radius: 999px;
    background: #e8edf2;
`;

const Segment = styled.div<{ tone: MetricTone; width: number }>`
    width: ${props => props.width}%;
    min-width: ${props => props.width > 0 ? '3px' : '0'};
    height: 100%;
    background: ${props => toneColor(props.tone)};
`;

const BreakdownGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 7px;

    ${breakpoint_large} {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    ${breakpoint_small} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const BreakdownCard = styled.div<{ tone: MetricTone }>`
    min-width: 0;
    padding: 10px;
    border: 1px solid ${props => toneBorder(props.tone)};
    border-radius: 10px;
    background: ${props => toneBackground(props.tone)};
`;

const BreakdownValue = styled.div`
    color: ${color_shades_darkest};
    font-size: 17px;
    font-weight: 750;
    line-height: 1;
`;

const BreakdownLabel = styled.div`
    margin-top: 5px;
    color: #455468;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.25;
`;

const BreakdownPercent = styled.div`
    margin-top: 4px;
    color: #59677a;
    font-size: 9px;
    font-weight: 650;
`;

const ReconciledBadge = styled.div<{ reconciled: boolean }>`
    padding: 6px 9px;
    border: 1px solid ${props => props.reconciled ? 'rgba(14, 133, 187, 0.2)' : 'rgba(71, 85, 105, 0.2)'};
    border-radius: 999px;
    background: ${props => props.reconciled ? '#f1f9fe' : '#f4f6f8'};
    color: ${props => props.reconciled ? '#0e85bb' : '#475569'};
    font-size: 9px;
    font-weight: 750;
    white-space: nowrap;

    ${breakpoint_small} {
        display: inline-block;
        margin-top: 7px;
    }
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;

    ${breakpoint_large} {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    ${breakpoint_small} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const MetricCard = styled.div<{ tone: MetricTone }>`
    min-height: 116px;
    padding: 15px;
    border: 1px solid ${props => toneBorder(props.tone)};
    border-radius: 13px;
    background: ${props => toneBackground(props.tone)};
`;

const MetricValue = styled.div`
    color: ${color_shades_darkest};
    font-size: 23px;
    font-weight: 750;
    letter-spacing: -0.5px;
    line-height: 1;
`;

const MetricLabel = styled.div`
    margin-top: 7px;
    color: #344357;
    font-size: 11px;
    font-weight: 720;
    line-height: 1.3;
`;

const MetricMeta = styled.div<{ tone: MetricTone }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 7px;
    padding: 3px 6px;
    border-radius: 999px;
    background: ${props => toneBorder(props.tone)};
    color: ${props => toneColor(props.tone)};
    font-size: 9px;
    font-weight: 750;
    line-height: 1.3;
`;

const MetricMetaInfo = styled.span`
    display: inline-flex;
    width: 13px;
    height: 13px;
    align-items: center;
    justify-content: center;
    border: 1px solid currentColor;
    border-radius: 50%;
    font-size: 8px;
    font-weight: 800;
    line-height: 1;
    cursor: help;
    opacity: 0.8;
`;

const MetricDescription = styled.div`
    margin-top: 6px;
    color: #4a596c;
    font-size: 10px;
    line-height: 1.5;
`;

const FormulaBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 11px;
    padding: 11px 13px;
    border: 1px dashed #c9d2dc;
    border-radius: 10px;
    background: ${color_defaults_white};

    ${breakpoint_small} {
        display: block;
    }
`;

const FormulaLabel = styled.div`
    color: #59677a;
    font-size: 9px;
    font-weight: 750;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const FormulaValues = styled.div`
    color: #4a596c;
    font-size: 11px;
    line-height: 1.45;
    text-align: right;

    strong {
        margin-left: 8px;
        color: ${color_shades_darkest};
        font-weight: 750;
    }

    ${breakpoint_small} {
        margin-top: 5px;
        text-align: left;
    }
`;

export default AppointmentSummaryModal;
